import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { quoteApi, accountApi, documentApi, clientApi } from '@/services/api'
import { useClientStore } from '@/stores/clientStore'
import { formatCurrency } from '@/utils/currency'
import type { Account, Document } from '@/types'
import { Plus, Trash2, Paperclip, Upload, File, FileText, Image, Lock } from 'lucide-react'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'

const MAX_SIZE = 10 * 1024 * 1024
const ALLOWED_TYPES = [
  'application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
]

function DocIcon({ mimeType }: { mimeType: string }) {
  if (mimeType.startsWith('image/')) return <Image className="w-4 h-4 text-blue-400 shrink-0" />
  if (mimeType === 'application/pdf') return <FileText className="w-4 h-4 text-red-400 shrink-0" />
  return <File className="w-4 h-4 text-gray-400 shrink-0" />
}

function formatSize(bytes: number | null) {
  if (!bytes) return '—'
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface LineItemForm {
  description: string
  quantity: string
  unit_price: string
  account_id: string
  tax_rate: string
}

function emptyLine(): LineItemForm {
  return { description: '', quantity: '1', unit_price: '0', account_id: '', tax_rate: '0' }
}

function lineAmount(line: LineItemForm): number {
  const qty = parseFloat(line.quantity) || 0
  const price = parseFloat(line.unit_price) || 0
  return qty * price
}

function lineTax(line: LineItemForm): number {
  const amount = lineAmount(line)
  const rate = parseFloat(line.tax_rate) || 0
  return (amount * rate) / 100
}

export default function CreateQuote() {
  const { clientId, quoteId } = useParams<{ clientId: string; quoteId: string }>()
  const navigate = useNavigate()
  const isEdit = !!quoteId
  const setCurrentClient = useClientStore((s) => s.setCurrentClient)
  const [premiumGated, setPremiumGated] = useState(false)

  const today = new Date().toISOString().slice(0, 10)
  const in30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10)

  const [form, setForm] = useState({
    contact_name: '',
    contact_email: '',
    issue_date: today,
    expiry_date: in30,
    reference: '',
    title: '',
    summary: '',
    notes: '',
    terms: '',
    currency: 'USD',
  })
  const [lines, setLines] = useState<LineItemForm[]>([emptyLine()])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(isEdit)
  const [attachments, setAttachments] = useState<Document[]>([])
  const [uploading, setUploading] = useState(false)
  const attachInputRef = useRef<HTMLInputElement>(null)

  const revenueAccounts = accounts.filter((a) => a.account_type === 'revenue' && !a.is_archived)

  useEffect(() => {
    if (!clientId) return
    accountApi.list(clientId).then((res) => setAccounts(res.data.accounts)).catch(() => {})

    clientApi.get(clientId).then((res) => {
      const client = res.data.client
      setCurrentClient(client)
      if (client?.plan_tier !== 'premium') setPremiumGated(true)
      if (!isEdit && client?.currency) setForm((prev) => ({ ...prev, currency: client.currency }))
    }).catch(() => {})

    if (isEdit && quoteId) {
      documentApi.list(clientId).then((res) => {
        setAttachments(res.data.documents.filter((d: Document) => d.linked_quote_id === quoteId))
      }).catch(() => {})

      quoteApi
        .get(quoteId)
        .then((res) => {
          const q = res.data.quote
          if (q) {
            setForm({
              contact_name: q.contact_name,
              contact_email: q.contact_email,
              issue_date: q.issue_date,
              expiry_date: q.expiry_date || '',
              reference: q.reference,
              title: q.title,
              summary: q.summary,
              notes: q.notes,
              terms: q.terms,
              currency: q.currency,
            })
            setLines(
              q.line_items.map((li: { description: string; quantity: string; unit_price: string; account_id: string | null; tax_rate: string }) => ({
                description: li.description,
                quantity: li.quantity,
                unit_price: li.unit_price,
                account_id: li.account_id ?? '',
                tax_rate: li.tax_rate,
              }))
            )
          }
        })
        .catch(() => toast.error('Failed to load quote'))
        .finally(() => setLoading(false))
    }
  }, [clientId, quoteId, isEdit])

  const subTotal = lines.reduce((s, l) => s + lineAmount(l), 0)
  const taxTotal = lines.reduce((s, l) => s + lineTax(l), 0)
  const total = subTotal + taxTotal

  function setLine(index: number, patch: Partial<LineItemForm>) {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)))
  }

  function addLine() {
    setLines((prev) => [...prev, emptyLine()])
  }

  function removeLine(index: number) {
    if (lines.length === 1) return
    setLines((prev) => prev.filter((_, i) => i !== index))
  }

  function buildPayload() {
    return {
      client_ledger_id: clientId,
      contact_name: form.contact_name.trim(),
      contact_email: form.contact_email.trim(),
      issue_date: form.issue_date,
      expiry_date: form.expiry_date || null,
      currency: form.currency,
      reference: form.reference.trim(),
      title: form.title.trim(),
      summary: form.summary.trim(),
      notes: form.notes.trim(),
      terms: form.terms.trim(),
      line_items: lines.map((l, i) => ({
        description: l.description,
        quantity: l.quantity,
        unit_price: l.unit_price,
        account_id: l.account_id || null,
        tax_rate: l.tax_rate,
        sort_order: i,
      })),
    }
  }

  async function handleAttachmentUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !clientId || !quoteId) return
    if (file.size > MAX_SIZE) { toast.error('File exceeds 10 MB limit'); return }
    if (!ALLOWED_TYPES.includes(file.type)) { toast.error('File type not supported'); return }
    setUploading(true)
    try {
      const res = await documentApi.upload(clientId, file, 'quote', undefined, undefined, undefined, quoteId)
      setAttachments((prev) => [...prev, res.data])
      toast.success('Document attached')
    } catch { toast.error('Upload failed') }
    finally {
      setUploading(false)
      if (attachInputRef.current) attachInputRef.current.value = ''
    }
  }

  async function handleAttachmentDelete(doc: Document) {
    if (!clientId) return
    try {
      await documentApi.delete(clientId, doc.id)
      setAttachments((prev) => prev.filter((d) => d.id !== doc.id))
      toast.success('Attachment removed')
    } catch { toast.error('Failed to remove') }
  }

  async function handleSaveDraft(e: React.FormEvent) {
    e.preventDefault()
    if (!form.contact_name.trim()) {
      toast.error('Contact name is required')
      return
    }
    if (lines.every((l) => !l.description.trim())) {
      toast.error('At least one line item is required')
      return
    }
    setSaving(true)
    try {
      if (isEdit && quoteId) {
        const payload = {
          contact_name: form.contact_name.trim(),
          contact_email: form.contact_email.trim(),
          issue_date: form.issue_date,
          expiry_date: form.expiry_date || null,
          reference: form.reference.trim(),
          title: form.title.trim(),
          summary: form.summary.trim(),
          notes: form.notes.trim(),
          terms: form.terms.trim(),
          line_items: lines.map((l, i) => ({
            description: l.description,
            quantity: l.quantity,
            unit_price: l.unit_price,
            account_id: l.account_id || null,
            tax_rate: l.tax_rate,
            sort_order: i,
          })),
        }
        await quoteApi.update(quoteId, payload)
        toast.success('Quote updated')
      } else {
        await quoteApi.create(buildPayload())
        toast.success('Quote saved as draft')
      }
      navigate(`/clients/${clientId}/quotes`)
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to save quote'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveAndSend() {
    if (!form.contact_name.trim()) {
      toast.error('Contact name is required')
      return
    }
    if (lines.every((l) => !l.description.trim())) {
      toast.error('At least one line item is required')
      return
    }
    setSaving(true)
    try {
      const res = await quoteApi.create(buildPayload())
      const newId = res.data.quote.id
      await quoteApi.send(newId)
      toast.success('Quote created and marked as sent')
      navigate(`/clients/${clientId}/quotes`)
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to create quote'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  if (premiumGated) {
    return (
      <Card className="max-w-sm mx-auto mt-16">
        <CardContent className="p-10 text-center">
          <div className="w-12 h-12 bg-[#FFF3CD] rounded-[12px] flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6 text-[#856404]" />
          </div>
          <h2 className="text-[17px] font-bold text-[#000A1E] mb-2">Premium Feature</h2>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-5">
            Quotes requires a Premium client. Upgrade in Client Settings &rarr; Plan.
          </p>
          <Button onClick={() => navigate(`/clients/${clientId}/settings`)}>Upgrade to Premium</Button>
        </CardContent>
      </Card>
    )
  }

  if (loading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">
            {isEdit ? 'Edit Quote' : 'New Quote'}
          </h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">
            {isEdit ? 'Update quote details' : 'Create a new sales quote'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isEdit && (
            <Button type="button" variant="outline" onClick={handleSaveAndSend} disabled={saving}>
              Save &amp; Send
            </Button>
          )}
          <Button type="button" onClick={handleSaveDraft} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Save Draft'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate(`/clients/${clientId}/quotes`)}>
            Cancel
          </Button>
        </div>
      </div>

      <form onSubmit={handleSaveDraft} className="space-y-5">
        {/* Contact + Dates */}
        <Card>
          <CardHeader className="border-b px-5 py-4">
            <CardTitle className="text-[13px] font-semibold text-[#000A1E]">Quote Details</CardTitle>
          </CardHeader>
          <CardContent className="p-5 grid grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label>Contact Name <span className="text-red-500">*</span></Label>
              <Input
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                placeholder="Customer or company name"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                placeholder="customer@example.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Quote title (e.g. Website Redesign Proposal)"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Reference</Label>
              <Input
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="Reference number"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Issue Date <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Expiry Date</Label>
              <Input
                type="date"
                value={form.expiry_date}
                onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Input
                value={form.currency}
                readOnly
                disabled
                title="Currency is set from client settings and cannot be changed"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Summary</Label>
              <textarea
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                className="input"
                rows={2}
                placeholder="Brief summary of the quote"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="input"
                rows={2}
                placeholder="Notes visible on the quote"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Terms &amp; Conditions</Label>
              <textarea
                value={form.terms}
                onChange={(e) => setForm({ ...form, terms: e.target.value })}
                className="input"
                rows={2}
                placeholder="Payment terms and conditions"
              />
            </div>
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card className="overflow-hidden">
          <CardHeader className="border-b px-5 py-4">
            <CardTitle className="text-[13px] font-semibold text-[#000A1E]">Line Items</CardTitle>
          </CardHeader>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[30%]">Description</TableHead>
                  <TableHead className="text-right w-[8%]">Qty</TableHead>
                  <TableHead className="text-right w-[12%]">Unit Price</TableHead>
                  <TableHead className="w-[25%]">Account</TableHead>
                  <TableHead className="text-right w-[8%]">Tax %</TableHead>
                  <TableHead className="text-right w-[12%]">Amount</TableHead>
                  <TableHead className="w-[5%]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line, i) => {
                  const amt = lineAmount(line)
                  return (
                    <TableRow key={i}>
                      <TableCell className="py-2">
                        <Input
                          value={line.description}
                          onChange={(e) => setLine(i, { description: e.target.value })}
                          placeholder="Item description"
                        />
                      </TableCell>
                      <TableCell className="py-2">
                        <Input
                          type="number" step="0.0001" min="0"
                          value={line.quantity}
                          onChange={(e) => setLine(i, { quantity: e.target.value })}
                          className="text-right"
                        />
                      </TableCell>
                      <TableCell className="py-2">
                        <Input
                          type="number" step="0.01" min="0"
                          value={line.unit_price}
                          onChange={(e) => setLine(i, { unit_price: e.target.value })}
                          className="text-right"
                        />
                      </TableCell>
                      <TableCell className="py-2">
                        <select
                          value={line.account_id}
                          onChange={(e) => setLine(i, { account_id: e.target.value })}
                          className="input"
                        >
                          <option value="">— Select account —</option>
                          {revenueAccounts.map((a) => (
                            <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell className="py-2">
                        <Input
                          type="number" step="0.01" min="0" max="100"
                          value={line.tax_rate}
                          onChange={(e) => setLine(i, { tax_rate: e.target.value })}
                          className="text-right"
                        />
                      </TableCell>
                      <TableCell className="py-2 text-right text-[13px] text-[#000A1E] tabular-nums">
                        {formatCurrency(amt, form.currency)}
                      </TableCell>
                      <TableCell className="py-2 text-center">
                        <Button
                          type="button" variant="ghost" size="icon-sm"
                          onClick={() => removeLine(i)}
                          disabled={lines.length === 1}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
          <div className="px-5 py-3 border-t border-border flex items-start justify-between">
            <Button type="button" variant="ghost" onClick={addLine} className="text-xs text-[#0078C8] gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Add Line
            </Button>
            <div className="text-right space-y-1 min-w-[180px]">
              <div className="flex justify-between text-[13px] text-[rgba(0,10,30,0.75)]">
                <span>Subtotal</span>
                <span>{formatCurrency(subTotal, form.currency)}</span>
              </div>
              <div className="flex justify-between text-[13px] text-[rgba(0,10,30,0.75)]">
                <span>Tax</span>
                <span>{formatCurrency(taxTotal, form.currency)}</span>
              </div>
              <Separator />
              <div className="flex justify-between text-base font-semibold text-[#000A1E] pt-1">
                <span>Total ({form.currency})</span>
                <span>{formatCurrency(total, form.currency)}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Attachments — edit mode only */}
        {isEdit && (
          <Card>
            <CardHeader className="border-b px-5 py-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-[13px] font-semibold text-[#000A1E] flex items-center gap-2">
                  <Paperclip className="w-4 h-4 text-[rgba(0,10,30,0.75)]" />
                  Attachments
                  {attachments.length > 0 && (
                    <Badge variant="default" className="text-xs">{attachments.length}</Badge>
                  )}
                </CardTitle>
                <label className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), uploading ? 'opacity-50 pointer-events-none' : 'cursor-pointer', 'gap-1.5')}>
                  <Upload className="w-3.5 h-3.5" />
                  {uploading ? 'Uploading...' : 'Attach File'}
                  <input ref={attachInputRef} type="file" className="hidden" onChange={handleAttachmentUpload} disabled={uploading} accept={ALLOWED_TYPES.join(',')} />
                </label>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {attachments.length === 0 ? (
                <p className="px-5 py-6 text-[13px] text-[rgba(0,10,30,0.75)] text-center">No attachments yet</p>
              ) : (
                <div className="divide-y divide-border">
                  {attachments.map((doc) => (
                    <div key={doc.id} className="flex items-center gap-3 px-5 py-3">
                      <DocIcon mimeType={doc.mime_type} />
                      <a href={doc.file_path} target="_blank" rel="noreferrer"
                        className="flex-1 text-[13px] font-bold text-[#0078C8] hover:underline truncate">
                        {doc.file_name}
                      </a>
                      {doc.category && <Badge variant="default" className="capitalize text-xs">{doc.category}</Badge>}
                      <span className="text-xs text-[rgba(0,10,30,0.75)]">{formatSize(doc.file_size)}</span>
                      <Button variant="ghost" size="icon-sm" onClick={() => handleAttachmentDelete(doc)}
                        className="text-red-400 hover:text-red-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Bottom action buttons */}
        <div className="flex items-center gap-3">
          {!isEdit && (
            <Button type="button" variant="outline" onClick={handleSaveAndSend} disabled={saving}>
              Save &amp; Send
            </Button>
          )}
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Save Draft'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate(`/clients/${clientId}/quotes`)}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  )
}
